<!-- =========================================================================================
  File Name: EquipmentOperatorSidebar.vue
  Description: Link a single Operator to specific Equipment (or all Equipment), or vice versa.
  Supports selecting several items at once (bulk add) in a single submit.
========================================================================================== -->

<template>
  <vs-sidebar click-not-close position-right parent="body" default-index="1" color="primary"
    class="add-new-data-sidebar items-no-padding" spacer v-model="isSidebarActiveLocal">
    <div class="mt-6 flex items-center justify-between px-6">
      <h4>LINK EQUIPMENT &amp; OPERATOR</h4>
      <feather-icon icon="XIcon" @click.stop="isSidebarActiveLocal = false" class="cursor-pointer"></feather-icon>
    </div>
    <vs-divider class="mb-0"></vs-divider>

    <component :is="scrollbarTag" class="scroll-area--data-list-add-new" :settings="settings" :key="$vs.rtl">
      <div>
        <div class="vx-row pt-3 ml-2">
          <div class="vx-col">
            <span>Link Direction</span><br />
            <vs-switch class="rounded-pill" v-model="equipmentToOperatorMode" color="primary">
              <span slot="off">Employee &rarr; Equipment</span>
              <span slot="on">Equipment &rarr; Employee</span>
            </vs-switch>
          </div>
        </div>

        <div class="vx-row pt-3 m-2">
          <div class="vx-col w-full">
            <span>Employee Type</span>
            <v-select placeholder="Filter Employee list by type" :options="employeeTypeOptions" :clearable="false"
              v-model="employeeTypeFilter"></v-select>
          </div>
        </div>

        <template v-if="!equipmentToOperatorMode">
          <div class="vx-row pt-3 m-2">
            <div class="vx-col w-full">
              <span>Employee</span>
              <v-select placeholder="Select Employee" :options="filteredOperatorData" v-model="operatorSelect"></v-select>
            </div>
          </div>
          <div class="vx-row pt-3 ml-2">
            <div class="vx-col">
              <span>Assign To All Equipment</span>
              <vs-switch class="rounded-pill" v-model="assignToAll" color="danger">
                <span slot="off">No</span>
                <span slot="on">Yes</span>
              </vs-switch>
            </div>
          </div>
          <div class="vx-row pt-3 m-2" v-if="!assignToAll">
            <div class="vx-col w-full">
              <span>Equipment (select one or more)</span>
              <v-select placeholder="Select Equipment" :options="equipmentData" v-model="multiSelect" multiple></v-select>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="vx-row pt-3 m-2">
            <div class="vx-col w-full">
              <span>Equipment</span>
              <v-select placeholder="Select Equipment" :options="equipmentData" v-model="equipmentSelect"></v-select>
            </div>
          </div>
          <div class="vx-row pt-3 ml-2">
            <div class="vx-col">
              <span>Assign To All Employees</span>
              <vs-switch class="rounded-pill" v-model="assignToAll" color="danger">
                <span slot="off">No</span>
                <span slot="on">Yes</span>
              </vs-switch>
            </div>
          </div>
          <div class="vx-row pt-3 m-2" v-if="!assignToAll">
            <div class="vx-col w-full">
              <span>Employee (select one or more)</span>
              <v-select placeholder="Select Employee" :options="filteredOperatorData" v-model="multiSelect" multiple></v-select>
            </div>
          </div>
        </template>

        <div class="vx-row pt-3 ml-2">
          <div class="vx-col">
            <span>Access</span><br />
            <vs-switch class="rounded-pill" v-model="isBlocked" color="danger">
              <span slot="off">Allow</span>
              <span slot="on">Block</span>
            </vs-switch>
          </div>
        </div>

        <div class="vx-row pt-3 m-2" v-if="validationMessage">
          <div class="vx-col w-full">
            <vs-alert color="warning" :active="true">{{ validationMessage }}</vs-alert>
          </div>
        </div>
      </div>
    </component>
    <div class="flex flex-wrap items-center p-6" slot="footer">
      <vs-button class="rounded-lg m-1" color="success" type="gradient" @click="submitData" ref="submitButton"
        :disabled="!isFormValid" icon-pack="feather" icon="icon-check">Submit</vs-button>
      <vs-button class="rounded-lg m-1" color="danger" type="gradient" @click="isSidebarActiveLocal = false"
        icon-pack="feather" icon="icon-x-square">Cancel</vs-button>
    </div>
  </vs-sidebar>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vue-select";

export default {
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true
    }
  },
  components: {
    VuePerfectScrollbar,
    "v-select": vSelect
  },
  data() {
    return {
      equipmentToOperatorMode: false,
      operatorData: [],
      equipmentData: [],
      operatorSelect: null,
      equipmentSelect: null,
      multiSelect: [],
      assignToAll: false,
      isBlocked: false,
      employeeTypeOptions: [
        { label: "All Types", value: 0 },
        { label: "Driver", value: 1 },
        { label: "Fuel Attendant", value: 2 },
        { label: "Authorizer", value: 3 }
      ],
      employeeTypeFilter: { label: "All Types", value: 0 },
      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: 0.6
      }
    };
  },
  watch: {
    isSidebarActive(val) {
      if (!val) return;
      this.initValues();
      this.GetOperatorDropdown();
      this.GetEquipmentDropdown();
    },
    equipmentToOperatorMode() {
      this.multiSelect = [];
      this.assignToAll = false;
      this.isBlocked = false;
      this.employeeTypeFilter = { label: "All Types", value: 0 };
    }
  },
  computed: {
    isSidebarActiveLocal: {
      get() {
        return this.isSidebarActive;
      },
      set(val) {
        if (!val) {
          this.$emit("closeSidebar");
        }
      }
    },
    filteredOperatorData() {
      if (!this.employeeTypeFilter || !this.employeeTypeFilter.value) return this.operatorData;
      return this.operatorData.filter((item) => item.operatorTypeId === this.employeeTypeFilter.value);
    },
    validationMessage() {
      if (!this.equipmentToOperatorMode) {
        if (!this.operatorSelect || !this.operatorSelect.value) return "Select an Employee to continue.";
        if (!this.assignToAll && (!this.multiSelect || this.multiSelect.length === 0)) return "Select at least one Equipment, or enable 'Assign To All Equipment'.";
      } else {
        if (!this.equipmentSelect || !this.equipmentSelect.value) return "Select an Equipment to continue.";
        if (!this.assignToAll && (!this.multiSelect || this.multiSelect.length === 0)) return "Select at least one Employee, or enable 'Assign To All Employees'.";
      }
      return "";
    },
    isFormValid() {
      return this.validationMessage === "";
    },
    scrollbarTag() {
      return this.$store.getters.scrollbarTag;
    }
  },
  methods: {
    GetOperatorDropdown() {
      const self = this;
      var onSuccess = function (response) {
        self.operatorData = (response.data || []).map((item) => { return { label: item.name, value: item.id, operatorTypeId: item.operatorTypeId } });
      };
      var onFinally = function () { };
      self.$ajaxGet(self, "Operators/GetOperatordropdown", onSuccess, onFinally);
    },
    GetEquipmentDropdown() {
      const self = this;
      var onSuccess = function (response) {
        self.equipmentData = (response.data || []).map((item) => { return { label: item.registration, value: item.id } });
      };
      var onFinally = function () { };
      self.$ajaxGet(self, "Equipments/GetEquipmentDropDown", onSuccess, onFinally);
    },
    initValues() {
      this.equipmentToOperatorMode = false;
      this.operatorSelect = null;
      this.equipmentSelect = null;
      this.multiSelect = [];
      this.assignToAll = false;
      this.isBlocked = false;
      this.employeeTypeFilter = { label: "All Types", value: 0 };
    },
    showLoadingOnElement(element, scale, type) {
      var self = this;
      self.$vs.loading({
        type: type,
        container: self.$refs[element].$el,
        scale: scale
      });
    },
    submitData() {
      var self = this;
      if (!self.isFormValid) {
        self.FailedCustomNotify(self.validationMessage);
        return;
      }

      var onSuccess = function () {
        self.$emit("saved");
        self.isSidebarActiveLocal = false;
        self.initValues();
      };
      var onFinally = function () {
        self.$vs.loading.close(self.$refs.submitButton.$el);
      };

      if (!self.equipmentToOperatorMode) {
        if (self.assignToAll) {
          self.$ajaxPost(
            self,
            `PerEquipmentOperators/AssignOperatorToEquipment?operatorId=${self.operatorSelect.value}&allEquipment=true&isBlocked=${self.isBlocked}`,
            {},
            onSuccess,
            onFinally
          );
        } else {
          const obj = { OperatorId: self.operatorSelect.value, EquipmentIds: self.multiSelect.map((i) => i.value), IsBlocked: self.isBlocked };
          self.$ajaxPost(self, "PerEquipmentOperators/BulkAssignEquipmentToOperator", obj, onSuccess, onFinally);
        }
      } else {
        if (self.assignToAll) {
          self.$ajaxPost(
            self,
            `PerEquipmentOperators/AssignEquipmentToOperator?equipmentId=${self.equipmentSelect.value}&allOperators=true&isBlocked=${self.isBlocked}`,
            {},
            onSuccess,
            onFinally
          );
        } else {
          const obj = { EquipmentId: self.equipmentSelect.value, OperatorIds: self.multiSelect.map((i) => i.value), IsBlocked: self.isBlocked };
          self.$ajaxPost(self, "PerEquipmentOperators/BulkAssignOperatorsToEquipment", obj, onSuccess, onFinally);
        }
      }
    },
    FailedCustomNotify(msg) {
      this.$vs.notify({
        title: 'Not Successfull',
        text: msg,
        color: 'danger'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.add-new-data-sidebar {
  ::v-deep .vs-sidebar--background {
    z-index: 52010;
  }

  ::v-deep .vs-sidebar {
    z-index: 52010;
    width: 700px;
    max-width: 90vw;
  }
}

.scroll-area--data-list-add-new {
  height: calc(var(--vh, 1vh) * 100 - 16px - 45px - 82px);

  &:not(.ps) {
    overflow-y: auto;
  }
}
</style>
